/* System defined core library */
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { SelectedExamDetailService } from '../../../scheduling/shared/services/selected-exam-detail.service';
import { UtilityService } from '../../services/utility.service';

/* User defined models */
import { ProSchedulerDatePicker } from '../../models/common/datepicker.model';
import { ProSchedulerDropdown } from '../../models/common/dropdown.model';
import { DynamicFormBase } from '../../models/common/dynamic-form-base.model';
import { ProSchedulerLinkedCombo } from '../../models/common/linkedCombo.model';
import { Candidate } from '../../models/scheduling/candidate.model';
import { Eligibility } from '../../models/scheduling/eligibility.model';
import { State } from '../../models/scheduling/state.model';

/* User defined enums/constants */
import { regex as rx } from '../../../shared/constants/scheduling.constant';

@Component({
  selector: 'dynamic-control',
  templateUrl: './dynamic-form-control.component.html'
})
export class DynamicFormControlComponent implements OnInit, AfterViewInit {

  /* User defined private/public variables  */
  public eligibility: Eligibility;
  public regions: State[];
  public candidate: Candidate = new Candidate();
  public selectedCandidate: Candidate = new Candidate();
  public datepickerOptions: kendo.ui.DatePickerOptions = {};
  public isDobValid: boolean = true;
  public isEligibilityRequired: boolean = true;
  public selectedStartDate: any;
  public defaultMonth: string;
  public preApprovedSelection: string = 'no';
  public placeHolderText: string;
  public disabledDatepickerValue: any;
  public datePickerDialogText = 'Date picker';
  public invalidReadOnly: boolean = false;

  /* Input-OutPut parameters decalartions */
  @Input() ctrlInputCollection: DynamicFormBase<any>[];
  @Input() ctrlInputData: DynamicFormBase<any>;
  @Input() form: FormGroup;
  @Input() formStyle: string = '';
  @Input('inputItemList') inputItems: any[];
  @Output() isDynamicFormValid = new EventEmitter<boolean>();
  @Output() isSelectionChange = new EventEmitter<any>();

  /* Service subscriptions */
  eligibility$: Subscription;
  candidate$: Subscription;
  translateLanguage$: Subscription;
  preApprovedSelection$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {

    this.eligibility$ = selectedExamDetailService.eligibility.subscribe(
      eligibility => {
        this.eligibility = eligibility;
      });

    this.candidate$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.selectedCandidate = candidate;
      });

    this.preApprovedSelection$ = selectedExamDetailService.preapprovedSelection.subscribe(
      selection => {
        this.preApprovedSelection = selection;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.isEligibilityRequired = this.selectedExamDetailService.isEligibilityRequired;
    this.placeHolderText = this.selectedExamDetailService.dateFormat.toUpperCase();
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit(): void {
    // datepicker: birth date we set the default month back 16 years otherwise this month
    const today = moment();
    const year = this.ctrlInputData.key === 'BIRTH_DATE' ? today.subtract(16, 'years').year() : today.year();
    const month = (today.month() + 1).toString();
    this.defaultMonth = (String('0' + month).slice(-2)) + '/' + year;

    this.inputItems = [];

    if (this.ctrlInputData.value == undefined || this.ctrlInputData.value == null) {
      if (this.ctrlInputData.controlType !== 'linkedCombo') {
        this.ctrlInputData.value = '';
      } else {
        this.ctrlInputData.setValue('');
      }
    } else if (this.ctrlInputData.controlType === 'datepicker' && !!this.ctrlInputData.value) {
      this.disabledDatepickerValue = moment(!!this.ctrlInputData.value.jsdate ? this.ctrlInputData.value.jsdate : this.ctrlInputData.value).format(this.placeHolderText);
    }

    if (this.selectedCandidate != null && this.selectedCandidate != undefined &&
      this.selectedCandidate.details != null && this.selectedCandidate.details != undefined && this.formStyle == 'vertical') {
      this.setControlInputData(this.selectedCandidate);
    }

    if (!!this.eligibility && this.formStyle != 'vertical') {
      let eligibilityKeys = this.eligibility.eligibilityDetails._keys;

      for (let i: number = 0; i < eligibilityKeys.length; i++) {
        let eligibilityInputValue = this.eligibility.eligibilityDetails[this.ctrlInputData.key];

        if (eligibilityKeys[i] == this.ctrlInputData.key && !this.isNullOrUndefined(eligibilityInputValue)) {
          if (this.ctrlInputData.controlType !== 'linkedCombo') {
            this.ctrlInputData.value = eligibilityInputValue;
          } else {
            this.ctrlInputData.setValue(eligibilityInputValue);
          }
        }
      }
      this.isDynamicFormValid.emit(true);
    }

    // for datepicker
    this.datepickerOptions = {
      format: this.selectedExamDetailService.dateFormat,
      culture: this.selectedExamDetailService.browserCulture,
      start: "decade"
    }

    // for birthday
    if (this.ctrlInputData.key === 'BIRTH_DATE') {
      this.datePickerDialogText = "Date of Birth date picker";
      this.datepickerOptions = {
        ...this.datepickerOptions,
        max: new Date()
      };
    }
  }

  ngAfterViewInit(): void {
    this.disableReadOnlyInput();
  }

  /* Set the entered eligibility data in shared service if it is valid */
  onBlur($event: any, ctrlInputData: any, ctrlInputCollection: any): void {
    this.onChangeEligibilityFromControl($event, ctrlInputData, ctrlInputCollection);
  }

  /* Validate candidate data is valid or not and set in shared service*/
  setDynamicFormValid(ctrlInputData: any, ctrlInputCollection: DynamicFormBase<any>[]): void {
    let tempRequiredState: boolean = true;
    // for each item in the profile: if it is a datepicker grab the jsdate value if it exists
    for (let i: number = 0; i < this.selectedCandidate.details._keys.length; i++) {
      if (this.selectedCandidate.details._keys[i] == ctrlInputData.key) {
        this.selectedCandidate.details[this.ctrlInputData.key] = this.form.controls[ctrlInputData.key].value;

        if (ctrlInputData.controlType === 'datepicker') {
          if (ctrlInputData.value && ctrlInputData.value.jsdate) {
            this.selectedCandidate.details[ctrlInputData.key] = ctrlInputData.value.jsdate;
          } else {
            this.selectedCandidate.details[ctrlInputData.key] = ctrlInputData.value;
          }
        }
      }
    }

    // for each control in the collection: if it is a datepicker override the error on the form if it exists
    ctrlInputCollection.forEach(ctrl => {
      if (ctrl.controlType === 'datepicker') {
        this.form.controls[ctrl.key] && this.form.controls[ctrl.key].setErrors(null);
      }
    });

    // for each formcontrol on form: find the control in the collection for it, if it's a datepicker check the value in candidate, else check the value on the form item
    for (var property in this.form.controls) {
      tempRequiredState = this.form.valid || this.form.disabled;
      const tempData = ctrlInputCollection.find((x: any) => x.key === property && x.required && x.displayFlag);
      if (tempData && tempData.controlType === 'datepicker') {
        if (this.selectedCandidate.details[property] === '' || this.selectedCandidate.details[property] === null) {
          tempRequiredState = false;
          break; // if any required ctrl value is not there then set button disable flag false and break loop to check
        }
      } else if (tempData && (this.form.controls[property].value == '' || this.form.controls[property].value == null)) {
        tempRequiredState = false;
        break; // if any required ctrl value is not there then set button disable flag false and break loop to check
      }
    }

    if (tempRequiredState) {
      this.selectedExamDetailService.SetCandidateDataSource(this.selectedCandidate);
      this.isDynamicFormValid.emit(true);
    }
    else {
      this.isDynamicFormValid.emit(false);
    }
  }

  public isNullOrUndefined(object: any): boolean {
    if (object == null || object == undefined || object == "") {
      return true;
    }
    else { return false; }
  }

  /* Set the entered candidate data in shared service if it is valid */
  onBlurCandidateFromControl($event: any, ctrlInputData: any, ctrlInputCollection: any): void {
    if (this.isValid) {
      this.setDynamicFormValid(ctrlInputData, ctrlInputCollection);
    } else {
      this.isDynamicFormValid.emit(false);
    }
  }

  // Set control input data key value so that when change event fire , in form collection checkbox control will reflected
  setControlInputDataValue($event: any, ctrlInputData: any): void {
    if (this.isValid) {
      ctrlInputData.value = $event.checked;
      this.isDynamicFormValid.emit(true);
    } else {
      this.isDynamicFormValid.emit(false);
    }
  }

  /* Validate Eligibility data is valid or not and set in shared service*/
  onChangeEligibilityFromControl($event: any, ctrlInputData: any, ctrlInputCollection: any): void {
    let tempRequiredState: boolean = true;

    if (this.eligibility != null && this.eligibility != undefined) {
      for (var property in this.form.controls) {
        var tempData = ctrlInputCollection.find((x: any) => x.key === property && x.required)
        if (tempData && (this.form.controls[property].value == "" || this.form.controls[property].value == null)) {
          tempRequiredState = false;
        }
      }
      for (let i: number = 0; i < this.eligibility.eligibilityDetails._keys.length; i++) {
        if (this.eligibility.eligibilityDetails._keys[i] == ctrlInputData.key) {
          this.eligibility.eligibilityDetails[this.ctrlInputData.key] = this.form.controls[ctrlInputData.key].value;
        }
      }
      if (tempRequiredState) {
        this.selectedExamDetailService.setEligibility(this.eligibility);
        this.isDynamicFormValid.emit(true);
      }
      else {
        this.isDynamicFormValid.emit(false);
      }
    }
  }

  /* Show data in edit mode from shared service */
  setControlInputData(dataObject: any) {

    this.ctrlInputData.value = this.selectedCandidate.details[this.ctrlInputData.key];

    for (let i: number = 0; i < dataObject.details._keys.length; i++) {

      if ((this.ctrlInputData.key == 'FIRST_NAME' || this.ctrlInputData.key == 'LAST_NAME')) {
        if (this.eligibility != null) {
          this.ctrlInputData.value = (this.eligibility.eligibilityDetails[this.ctrlInputData.key] != null &&
            this.eligibility.eligibilityDetails[this.ctrlInputData.key] != undefined && this.selectedCandidate.details[this.ctrlInputData.key] == '') ?
            this.eligibility.eligibilityDetails[this.ctrlInputData.key] : this.selectedCandidate.details[this.ctrlInputData.key];
          if (this.ctrlInputData.value != '' && this.ctrlInputData.value != null && this.ctrlInputData.value != undefined) {
            this.selectedCandidate.details[this.ctrlInputData.key] = this.ctrlInputData.value;
          }
        }
      }

      if (this.ctrlInputData.controlType === 'datepicker') {
        this.ctrlInputData.value = this.selectedCandidate.details[this.ctrlInputData.key];
        this.selectedStartDate = this.ctrlInputData.value;
        if (
          (this.ctrlInputData.value !== null && this.ctrlInputData.value !== undefined && this.ctrlInputData.value !== '') &&
          (this.ctrlInputData['rawDate'] && this.ctrlInputData['rawDate']['date'])
        ) {
          this.selectedStartDate = this.ctrlInputData['rawDate'];
        }
      } else if (this.ctrlInputData.key === 'COUNTRY') {
        (<ProSchedulerDropdown>this.ctrlInputData).setSelectedOptionInternally();
      }

      if (!!this.selectedCandidate.defaultDetails) {
        if (this.ctrlInputData.key === 'FIRSTNAME_RSA') {
          this.ctrlInputData.value = !!this.selectedCandidate.defaultDetails['first_name'] ? this.selectedCandidate.defaultDetails['first_name'] : this.selectedCandidate.details[this.ctrlInputData.key];
        }

        if (this.ctrlInputData.key === 'LASTNAME_RSA') {
          this.ctrlInputData.value = !!this.selectedCandidate.defaultDetails['last_name'] ? this.selectedCandidate.defaultDetails['last_name'] : this.selectedCandidate.details[this.ctrlInputData.key];
        }

        if (this.ctrlInputData.key === 'Work_Address_Line_1') {
          this.ctrlInputData.value = this.selectedCandidate.defaultDetails['address1'];
        }

        if (this.ctrlInputData.key === 'Town / City') {
          this.ctrlInputData.value = this.selectedCandidate.defaultDetails['city'];
        }

        if (this.ctrlInputData.key === 'Work_Address_Line_2') {
          this.ctrlInputData.value = this.selectedCandidate.defaultDetails['address2'];
        }

        if (this.ctrlInputData.key === 'Work_Address_Line_3') {
          this.ctrlInputData.value = this.selectedCandidate.defaultDetails['address3'];
        }

        if (this.ctrlInputData.key === 'Work_Address_Line_4') {
          this.ctrlInputData.value = this.selectedCandidate.defaultDetails['address4'];
        }
      }
    }
    // When Reversed back on candidate page the next button will enable because we assume all candidate required fields has been fill.
    this.isDynamicFormValid.emit(dataObject.isAllRequiredValuesExist);
  }

  /*
  * returns true only if the field is valid,
  * returns false if the field is tocuhed (returns trie only on blur) and the value is invalid
  */
  get isValid() {
    return (!(this.form.controls[this.ctrlInputData.key].touched && this.form.controls[this.ctrlInputData.key].invalid)
      || this.form.controls[this.ctrlInputData.key].pristine);
  }

  /**
   * check if the standard invalid demographic error be displayed
   * returns false if input is valid or demographic is email
   * @param ctrlInputData - metadata
   */
  shouldHide(ctrlInputData: any) {
    return this.isValid
      || ctrlInputData.validationMessage == ''
      || ctrlInputData.key === 'VALIDATE_EMAIL' || ctrlInputData.key === 'EMAIL';
  }

  /**
   * check if the email pattern error should be displayed
   * returns false if input is invalid or demographic is not email
   * @param ctrlInputData - metadata
   */
  invalidEmailPattern(ctrlInputData: any) {
    return (ctrlInputData.key === 'VALIDATE_EMAIL' || ctrlInputData.key === 'EMAIL')
      && this.form.controls[ctrlInputData.key].errors && this.form.controls[ctrlInputData.key].errors['pattern'];
  }

  /**
   * check if the email match error should be displayed
   * returns false if input doesn't match other entered email or demographic is not email
   * @param ctrlInputData - metadata
   */
  noMatch(ctrlInputData: any) {
    return (ctrlInputData.key === 'VALIDATE_EMAIL' || ctrlInputData.key === 'EMAIL')
      && this.form.controls[ctrlInputData.key].errors && this.form.controls[ctrlInputData.key].errors['notSame'];
  }

  /**
   * returns regex for textbox input
   * if no validationdata is provided for regex we provided regex with no boundries(.*)
   * inserts additional regex to deny Chars that are used for injections
   * does not support CHAR='' style validation, only REGEX="" and null
   * @param validationdata - string: '' | 'REGEX="{regex}"'
   */
  getPattern(validationdata: any): string {
    let regex: string;
    if (!validationdata || validationdata === 'NULL') {
      regex = '.*';
    } else if (validationdata.includes('REGEX=')) {
      regex = validationdata.split(';')[0];
      regex = regex.replace(/["]+/g, '');
      regex = regex.replace('REGEX=', '');
    } else if (validationdata.includes('CHAR=')) {
      let data: string[] = validationdata.split(';');
      let chars: string;
      let minLength: string;
      let maxLength: string;
      let start: string;
      let end: string;
      data.forEach((rule: string) => {
        if (rule.includes('CHAR=')) {
          chars = rule.replace(/[']+/g, '');
          chars = chars.replace('CHAR=', '');
        }
        if (rule.includes('Min=')) {
          minLength = rule.replace('Min=', '');
        }
        if (rule.includes('Max=')) {
          maxLength = rule.replace('Max=', '');
        }
        if (rule.includes('Start=')) {
          start = rule.replace(/[']+/g, '');
          start = start.replace('Start=', '');
        }
        if (rule.includes('End=')) {
          end = rule.replace(/[']+/g, '');
          end = end.replace('End=', '');
        }
      });

      // Convert provided valid characters to regular expression syntax, concat prefix/suffix if Start/End values are present
      regex = (start ? '(' + start + ')' : '') + (chars ? '[' + chars + ']*' : '.*') + (end ? '(' + end + ')' : '');
      // Uses positive lookahead to validate length if Min/Max values are present
      regex = minLength || maxLength ? '(?=.{' + (minLength ? minLength : '0') + ',' + (maxLength ? maxLength : '') + '}$)' + regex : regex;
    }
    // Uses positive and negative lookahead to reject if Greater Than (>), Less Than (<), Double Quote ("), or Semicolon (;) are present
    regex = rx.blackList + regex;
    this.ctrlInputData.pattern = regex;
    return regex;
  }

  /* Set changed country info in shared service */
  changeCountry(data: any, ctrlInputData: any, ctrlInputCollection: any): void {
    (<ProSchedulerDropdown>this.ctrlInputData).selectedOption = data;
    this.isSelectionChange.emit({
      type: 'CountryChanged',
      value: data
    });
    if (this.selectedCandidate && this.selectedCandidate.details) {
      if (this.selectedCandidate.details['COUNTY_NAME']) {
        this.selectedCandidate.details['COUNTY_NAME'] = null;
        this.selectedExamDetailService.SetCandidateDataSource(this.selectedCandidate);
      }
      if (this.selectedCandidate.details['STATE']) {
        this.selectedCandidate.details['STATE'] = null;
        this.selectedExamDetailService.SetCandidateDataSource(this.selectedCandidate);
      }
    }
    if (data != null && data != undefined) {
      data.key = ctrlInputData.key;
    } else {
      data = { key: 'COUNTRY' };
    }
    this.setDynamicFormValid(data, ctrlInputCollection);
  }

  /* Set changed state info in shared service */
  changeState(data: any, ctrlInputData: any, ctrlInputCollection: any): void {
    (<ProSchedulerDropdown>this.ctrlInputData).selectedOption = data;
    if (data != null && data != undefined) {
      data.key = ctrlInputData.key;
    }
    else {
      data = { key: ctrlInputData.key };
    }
    this.setDynamicFormValid(data, ctrlInputCollection);
  }

  changeValue(data: any, ctrlInputData: any, ctrlInputCollection: any): void {
    (<ProSchedulerDropdown>this.ctrlInputData).selectedOption = data;

    data = { key: ctrlInputData.key };

    if (!this.form.controls[ctrlInputData.key].invalid) {
      this.setDynamicFormValid(data, ctrlInputCollection);
    } else {
      this.isDynamicFormValid.emit(false);
    }
  }

  changeLinkedValue(parent: boolean, index: number, ctrlInputData: ProSchedulerLinkedCombo, ctrlInputCollection: DynamicFormBase<string>[]): void {
    const data = { key: ctrlInputData.key };
    this.form.controls[ctrlInputData.key].markAsTouched();
    this.form.controls[ctrlInputData.key].markAsDirty();

    if (parent) {
      if (index === 0) {
        ctrlInputData.setValue('');
      } else {
        ctrlInputData.setValue(ctrlInputData.values[index - 1].children[0]);
      }
    } else {
      ctrlInputData.value = ctrlInputData.childValues[index];
    }
    this.form.controls[ctrlInputData.key].setValue(ctrlInputData.value);
    this.setDynamicFormValid(data, ctrlInputCollection);
  }

  /**
   * check if the validationData includes a "memo" keyword
   * and split the data to get label
   * @param ctrlInputData - metadata
   */
  getLabelVertical(ctrlInputData: any) {
    if (ctrlInputData && ctrlInputData.validationData !== undefined) {
      if (ctrlInputData.validationData.includes('Memo=')) {
        return this.checkLabelForTranslations(ctrlInputData.alternativeLabel, ctrlInputData.validationData.split('Memo=')[1]);
      } else {
        return this.checkLabelForTranslations(ctrlInputData.key, ctrlInputData.label);
      }
    }
    return ctrlInputData.key;
  }

  getLabelHorizontal(ctrlInputData: any) {
    return this.checkLabelForTranslations(ctrlInputData.alternativeLabel, ctrlInputData.label);
  }

  /**
   * gets shared value for ID on error and aria-describedby on input
   * @param ctrlInputData - metadata
   */
  getErrorId(ctrlInputData: any, modifier: string): string {
    if (!!ctrlInputData && ctrlInputData.key) {
      return 'ERROR_ID_' + this.ctrlInputData.key + (!!modifier ? '_' + modifier : '');
    }

    return '';
  }

  checkLabelForTranslations(translatable: string, alternative: string) {
    let label = alternative;
    this.translate.get(translatable, {}).subscribe((res: string) => {
      if (translatable.toLowerCase() !== res.toLowerCase()) {
        label = res;
      }
    });
    return label
  }

  /* Set Start date in shared service */
  setSelectedStartDate(value: any, ctrlInputData: ProSchedulerDatePicker, ctrlInputCollection: any): void {
    var year = value && value['date'] ? value['date']['year'] : null;
    var month = value && value['date'] ? value['date']['month'] : null;
    var day = value && value['date'] ? value['date']['day'] : null;
    ctrlInputData.rawDate = value;
    if (year && month && day) {
      if (value != null && value != undefined && value.jsdate != null && value.jsdate != undefined) {
        this.ctrlInputData.value = value.jsdate;
        ctrlInputData.value = this.ctrlInputData.value;
        ctrlInputData.rawDate = value;
        this.selectedStartDate = value;
        this.onBlurCandidateFromControl(value, ctrlInputData, ctrlInputCollection);
      }
      this.isDobValid = true;
    }
    else {
      ctrlInputData.value = null;
      this.onBlurCandidateFromControl(null, ctrlInputData, ctrlInputCollection);
      this.isDobValid = false;
    }
  }

  comboChildRequired(ctrlInputData: DynamicFormBase<string>): boolean {
    return ctrlInputData.required || !!ctrlInputData.value;
  }

  /* Check form control validity before disabling if field is readonly */
  disableReadOnlyInput(): void {
    if (this.ctrlInputData.readOnly && !!this.form.controls[this.ctrlInputData.key]) {
      if (this.form.controls[this.ctrlInputData.key].invalid) {
        this.invalidReadOnly = true
      }
      this.form.controls[this.ctrlInputData.key].disable();
    }
    if (this.ctrlInputData.readOnly && !!this.form.controls[this.ctrlInputData.key + '_parent']) {
      if (this.form.controls[this.ctrlInputData.key + '_parent'].invalid) {
        this.invalidReadOnly = true
      }
      this.form.controls[this.ctrlInputData.key + '_parent'].disable();
    }
  }

  /* Toggle disabled state on form control to recheck validity */
  validateDisabledInput(): void {
    if (!!this.form.controls[this.ctrlInputData.key] && this.form.controls[this.ctrlInputData.key].disabled) {
      this.form.controls[this.ctrlInputData.key].enable();
      this.invalidReadOnly = false;
    }
    if (!!this.form.controls[this.ctrlInputData.key + '_parent'] && this.form.controls[this.ctrlInputData.key + '_parent'].disabled) {
      this.form.controls[this.ctrlInputData.key + '_parent'].enable();
      this.invalidReadOnly = false;
    }
    this.disableReadOnlyInput();
  }
}
